import React from 'react';
import {
  EstadoCotacaoOnline,
  fetchPesquisasDoConsumo,
  PesquisaDoConsumo,
  ResumoConsumoCotacaoOnline,
  TotaisCotacaoOnline,
} from '../../../../api/orcamentoCiliaApi';
import { ColunaTabela } from '../../../../components/sync-painel/TabelaSistema';
import { CardDeTabela, CartaoDeTotais } from './AbaRadarAssistente';
import ListaPaginada from './ListaPaginada';
import { fmtInt, fmtMes, fmtQuando, fmtUsd, nomeDoUsuario } from './formatacao';

/**
 * Aba "Cotação online": as pesquisas de preço de peças da tela de Requisições (at-15). O consumo é
 * gravado no backend de Requisições, uma linha por pesquisa — é de lá que esta aba lê.
 */

const SITUACAO: Record<PesquisaDoConsumo['status'], string> = {
  PENDENTE: 'Na fila',
  EM_ANDAMENTO: 'Pesquisando',
  CONCLUIDA: 'Concluída',
  FALHOU: 'Falhou',
};

/** Para quê: a peça e a requisição, mais o que disparou a pesquisa. */
function paraQue(p: PesquisaDoConsumo): string {
  const peca = p.peca ? p.peca.descricao : 'Peça não encontrada';
  const onde = [p.placa, p.numeroCilia ? `Cilia ${p.numeroCilia}` : null].filter(Boolean).join(' · ');
  return onde ? `${peca} (${onde})` : peca;
}

function porQue(p: PesquisaDoConsumo): string {
  const nivel = p.nivel === 'CUIDADOSO' ? 'cuidadoso' : 'econômico';
  return p.origem === 'REBUSCA' ? `Rebusca, nível ${nivel}` : `Cotação da requisição, nível ${nivel}`;
}

const COLUNAS: ColunaTabela<PesquisaDoConsumo>[] = [
  { titulo: 'Quando', largura: 140, texto: (p) => fmtQuando(p.criadaEm) },
  // Na automática, quem disparou é quem concluiu a requisição — a pesquisa nasce desse clique.
  { titulo: 'Quem', largura: 160, larguraMaxima: 220, texto: (p) => p.disparadaPorNome ?? 'Não registrado' },
  { titulo: 'Para quê', largura: 280, larguraMaxima: 380, texto: paraQue },
  { titulo: 'Como', largura: 200, texto: porQue },
  { titulo: 'Situação', largura: 100, texto: (p) => SITUACAO[p.status] },
  { titulo: 'Buscas', largura: 80, texto: (p) => fmtInt(p.buscas) },
  { titulo: 'Custo estimado', largura: 120, texto: (p) => fmtUsd(p.custoUsd) },
];

type PorUsuario = ResumoConsumoCotacaoOnline['porUsuario'][number];
type PorMes = ResumoConsumoCotacaoOnline['porMes'][number];

const COLUNAS_POR_USUARIO: ColunaTabela<PorUsuario>[] = [
  {
    titulo: 'Usuário',
    largura: 180,
    larguraMaxima: 240,
    texto: (u) => (u.usuarioId ? u.nome ?? 'Usuário não encontrado' : nomeDoUsuario(null, null)),
  },
  { titulo: 'Pesquisas', largura: 100, texto: (u) => fmtInt(u.pesquisas) },
  { titulo: 'Buscas', largura: 100, texto: (u) => fmtInt(u.buscas) },
  { titulo: 'Custo estimado', largura: 120, texto: (u) => fmtUsd(u.custoUsd) },
];

const COLUNAS_POR_MES: ColunaTabela<PorMes>[] = [
  { titulo: 'Mês', largura: 150, texto: (m) => fmtMes(m.mes) },
  { titulo: 'Pesquisas', largura: 100, texto: (m) => fmtInt(m.pesquisas) },
  { titulo: 'Buscas', largura: 100, texto: (m) => fmtInt(m.buscas) },
  { titulo: 'Tokens (entrada / saída)', largura: 170, texto: (m) => `${fmtInt(m.tokensEntrada)} / ${fmtInt(m.tokensSaida)}` },
  { titulo: 'Custo estimado', largura: 120, texto: (m) => fmtUsd(m.custoUsd) },
];

const buscarPesquisas = (pagina: number) => fetchPesquisasDoConsumo(pagina);

function detalhe(t: TotaisCotacaoOnline): string {
  return `${fmtInt(t.pesquisas)} ${t.pesquisas === 1 ? 'pesquisa' : 'pesquisas'} · ${fmtInt(t.buscas)} buscas na internet`;
}

const AbaCotacaoOnline: React.FC<{
  resumo: ResumoConsumoCotacaoOnline;
  estado: EstadoCotacaoOnline | null;
}> = ({ resumo, estado }) => (
  <>
    {estado && (
      <p className="admin-hint" style={{ marginTop: 12 }}>
        Teto mensal:{' '}
        <strong>{estado.tetoMensalUsd != null ? fmtUsd(estado.tetoMensalUsd) : 'não definido'}</strong>
        {estado.motivoParado ? ` · Parada: ${estado.motivoParado}` : ' · Ligada'}. O teto é definido em
        Administração do Sistema, Requisições.
      </p>
    )}

    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
      <CartaoDeTotais titulo="Hoje" custo={resumo.hoje.custoUsd} detalhe={detalhe(resumo.hoje)} />
      <CartaoDeTotais titulo="Mês corrente" custo={resumo.mesCorrente.custoUsd} detalhe={detalhe(resumo.mesCorrente)} />
      <CartaoDeTotais
        titulo={`Últimos ${resumo.janelaDias} dias`}
        custo={resumo.ultimos30Dias.custoUsd}
        detalhe={detalhe(resumo.ultimos30Dias)}
      />
    </div>

    {resumo.porUsuario.length > 0 && (
      <CardDeTabela
        titulo={`Por usuário (últimos ${resumo.janelaDias} dias)`}
        colunas={COLUNAS_POR_USUARIO}
        itens={resumo.porUsuario}
        chave={(u) => u.usuarioId ?? 'sem-usuario'}
        vazio="Nenhuma pesquisa nos últimos 30 dias."
      />
    )}

    <ListaPaginada
      titulo="Cada pesquisa"
      dica={
        <>
          Uma linha por pesquisa de preço: quem pediu, para qual peça de qual requisição, como e
          quando. Na pesquisa <strong>automática</strong>, quem aparece é quem concluiu a requisição,
          porque é esse clique que dispara a pesquisa das peças.
        </>
      }
      buscar={buscarPesquisas}
      colunas={COLUNAS}
      chave={(p) => p.id}
      vazio="Nenhuma pesquisa de preço feita até agora."
      erro="Não foi possível carregar a lista de pesquisas. Recarregue a página; se persistir, acione a TI."
    />

    {resumo.porMes.length > 0 && (
      <CardDeTabela titulo="Por mês" colunas={COLUNAS_POR_MES} itens={resumo.porMes} chave={(m) => m.mes} vazio="Nenhuma pesquisa." />
    )}
  </>
);

export default AbaCotacaoOnline;
